import {Component, OnInit, Inject, OnChanges, SimpleChanges, EventEmitter, DestroyRef, inject} from '@angular/core';
import { FormGroup, FormBuilder } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { BehaviorSubject, throwError } from 'rxjs';
import { catchError, map, take, tap } from 'rxjs/operators';
import { TreeNode } from '@ali-hm/angular-tree-component';
import {MenuKeyContext} from '@emm/core/models/menu-key.model';
import {LocationSet} from '@emm/core/models/location-set.model';
import {MenuService} from '@emm/core/services/menu.service';
import {MenuStoreService} from '@emm/menu/services/menu-store.service';
import {NotificationService} from '@emm/shared/services/notification.service';
import {MenuTreeFacadeService} from '@emm/menu/services/menu-tree-facade.service';
import {MenuApiService} from '@emm/menu/services/menu-api.service';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {wrap} from '@emm/core/util/rx.util';
import {API_ERRORS} from "@emm/core/util/api-constants";

@Component({
    selector: 'emm-paste-child-item-dialog',
    styleUrls: ['./paste-child-item-dialog.component.scss'],
    templateUrl: './paste-child-item-dialog.component.html'
})
export class PasteChildItemDialogComponent implements OnInit, OnChanges {

    private readonly destroyRef: DestroyRef = inject(DestroyRef);

    color = '#e5173f';
    diameter = 25;
    featureScope: string;
    form: FormGroup;
    firstFormGroup: FormGroup;
    isLinear = true;
    matchCount: number;
    descendantCount: number;
    menuKey: MenuKeyContext;
    mode = 'indeterminate';
    options: LocationSet[];
    parent: TreeNode;
    copiedItem = this.data.copiedItem;
    parentAddPath;
    response;
    searched: boolean;
    secondFormGroup: FormGroup;
    spinner = false;
    subMenuTag;
    currentSubmenu;
    cascadeSubmenus$ = new BehaviorSubject([]);
    submenusCache = [];
    selectedSubmenus: string[] = [];
    bottomChips = [];
    selectedBottomChips = [];
    bottomChipsChanged$ = new EventEmitter();
    pathChanged$ = new EventEmitter();
    path;
    showPath = false;
    item;
    attributes = [];
    attributes$ = new BehaviorSubject([]);
    searchForm = this.formBuilder.group({searchTerm: ['']});

    filterSize: number;
    topSelected = new BehaviorSubject<boolean>(false);
    bottomSelected = new BehaviorSubject<boolean>(false);

    constructor(
        private formBuilder: FormBuilder,
        public dialogRef: MatDialogRef<PasteChildItemDialogComponent>,
        private menuService: MenuService,
        private menuStore: MenuStoreService,
        private notificationService: NotificationService,
        private treeFacade: MenuTreeFacadeService,
        private api: MenuApiService,
        @Inject(MAT_DIALOG_DATA) public data: any) {}

    ngOnInit(): void {
        this.featureScope = this.data.featureScope;
        this.parent = this.data.parent;
        this.menuKey = this.data.menuKey;
        this.treeFacade.resetCascadeSubmenus();
        this.listenForCascadeChange();
        this.api.getSubmenusByFeatureScope(this.featureScope, this.menuKey)
            .pipe(
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe({
                next: (result) => {
                    this.cascadeSubmenus$.next(result);
                    this.submenusCache = result;
                },
                error: (error) => {
                    this.notificationService.error(error?.error?.error?.message || API_ERRORS.GenericErrorMessage);
                }
            });

        this.firstFormGroup = this.formBuilder.group({});
        this.secondFormGroup = this.formBuilder.group({});

        this.loadTargetedItem().then(() => {
            this.menuStore.queryItemDetail.selected$
                .pipe(
                    takeUntilDestroyed(this.destroyRef)
                )
                .subscribe(item => {
                this.item = item;
                this.buildAttributes();
                setTimeout(() => {
                    this.findCurrentSubmenu();
                    this.initChips();
                    this.generatePath();
                }, 1);
            });
        });

        this.bottomChipsChanged$
            .pipe(
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe((chip) => {
            // validate deselecting intermediate parents (between bottom chip and top parent)
            if (!chip.selected === false) {
                const topSelectedParentIndex = this.bottomChips.findIndex((ch) => ch.selected);
                const currentSelectedIndex = this.bottomChips.findIndex((ch) => ch.tag === chip.tag);
                if (currentSelectedIndex > topSelectedParentIndex) {
                    this.notificationService.error('Cannot deselect intermediate parent');
                    return;
                }
            }

            // toggle selected chip
            const newValue = !chip.selected;

            // toggle descendant chips
            chip.selected = newValue;
            if (newValue) {
                // set all child descendants to true
                const match = this.bottomChips.indexOf(chip);
                this.bottomChips.filter((ch, index) => index > match).forEach((ch) => (ch.selected = true));
            }
            this.pathChanged$.emit();
        });

        // update json path
        this.pathChanged$
            .pipe(
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe(() => {
            this.generatePath();
        });
    }


    ngOnChanges(changes: SimpleChanges) {
        this.refreshCascadeSubmenus();
    }

    async loadTargetedItem() {
        await this.menuStore.dispatch.loadItemDetail(this.copiedItem);
    }

    refreshCascadeSubmenus() {
        this.treeFacade.selectedSubmenusPaste$
            .pipe(
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe(result => {
            for (const submenu of result) {
                if (this.selectedSubmenus.includes(submenu)) {
                    this.selectedSubmenus.filter(element => element !== submenu);
                } else {
                    this.selectedSubmenus.push(submenu);
                }
            }});
    }

    deselectCascadeSubmenus(submenuTag: string) {
        if (this.selectedSubmenus.includes(submenuTag)) {
            this.selectedSubmenus.splice(this.selectedSubmenus.indexOf((submenuTag)));
        }
    }

    onNoClick(): void {
        this.dialogRef.close();
    }

    onSave(): void {
        const result = {
            item: this.copiedItem.tag,
            parentPath: this.getPasteCascadePath(this.selectedBottomChips),
            submenuTag: this.subMenuTag,
            position: this.topSelected.value ? 'top' : 'bottom',
        };
        this.dialogRef.close(result);
    }

    togglePath(): void {
        this.showPath = !this.showPath;
    }

    createChip({name, tag}, selected: boolean): {} {
        return {name, selected, tag};
    }

    initChips(): void {
        // find item hierarchy
        const nodes = [];
        nodes.push(this.createChip(this.parent.data.data, true));
        this.findParents(this.parent).forEach((parent) =>
            nodes.push(this.createChip(parent.data.data, false))
        );
        const chips = nodes.reverse();
        this.bottomChips = chips.slice(1);
        if (this.bottomChips.length === 1) {
            this.bottomChips[0].selected = true;
        }
    }

    toggleSelectBottomChip(chip): void {
        this.bottomChipsChanged$.emit(chip);
    }

    generatePath(): void {
        this.path = null;
        const selectedBottomChips = this.bottomChips.filter((chip => chip.selected));
        this.selectedBottomChips = selectedBottomChips;
        this.path = this.getPath(selectedBottomChips);
        setTimeout(() => {
            this.findMatches(this.featureScope, this.path)
                .pipe(
                    takeUntilDestroyed(this.destroyRef)
                )
                .subscribe({
                        error: (error) => {
                            this.notificationService.error(error?.error?.error?.message || API_ERRORS.GenericErrorMessage);
                        }
                    }
                );
        }, 1);
    }

    getPath(selectedBottomChips): object | string {
        let fullTagPath = '';
        let ancestorMatchPath = '';
        let itemMatchPath = '';

        // more than 2 selected groups of chips is not allowed,
        if (selectedBottomChips.length === this.bottomChips.length) {
            for (const chip of selectedBottomChips) {
                fullTagPath += `/${chip.tag}`;
            }
            return {itemMatchPath: fullTagPath};
        } else {
            for (const chip of selectedBottomChips) {
                // if consecutive, add to ancestor match path
                if (selectedBottomChips.indexOf(chip) === this.bottomChips.indexOf(chip)) {
                    ancestorMatchPath += `/${chip.tag}`;
                }
                // if not consecutive, add to item match path
                else {
                    if (selectedBottomChips.length > 1) {
                        ancestorMatchPath.includes(selectedBottomChips[0].tag) ? ancestorMatchPath += `` :
                            ancestorMatchPath += `/${selectedBottomChips[0].tag}`;

                        for (let i = 1; i < selectedBottomChips.length; i++) {
                            itemMatchPath.includes(selectedBottomChips[i].tag) ||
                            ancestorMatchPath.includes(selectedBottomChips[i].tag) ? itemMatchPath += `` :
                                itemMatchPath += `/${selectedBottomChips[i].tag}`;
                        }
                    } else {
                        itemMatchPath += `/${chip.tag}`;
                    }
                }
            }
            return {
                ancestorMatchPath,
                itemMatchPath
            };
        }
    }

    getPasteCascadePath(selectedBottomChips): string {
        let parentMatchPath = '';
        for (const chip of selectedBottomChips) {
            parentMatchPath += `/${chip.tag}`;
        }
        return parentMatchPath;
    }

    getDescendantCount() {
        const path =this.getPasteCascadePath(this.selectedBottomChips);
        return this.menuService.countDescendantsForPasteCascade(path, this.copiedItem, this.selectedSubmenus);
    }

    findMatches(featureScope, path): any {
        this.getDescendantCount()
            .pipe(
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe({
                next: (result: number) => {
                    this.descendantCount = result;
                },
                error: (error) => {
                    this.notificationService.error(error?.error?.error?.message || API_ERRORS.GenericErrorMessage)
                }
            });
        const before = () => {
            this.spinner = true;
        };
        if (this.featureScope === 'Global' || this.featureScope === 'Viewer') {
            const source$ = this.menuService
                .countMatchesForUpdateCascade(featureScope, this.data.effectiveDate, this.menuKey, path, this.selectedSubmenus)
                .pipe(
                    tap((results) => {
                        this.spinner = false;
                        this.matchCount = results;
                    }),
                    catchError(() => {
                        const errorMessage = 'Error searching matches';
                        this.notificationService.error(errorMessage);
                        return throwError(errorMessage);
                    })
                );

            return wrap({
                before,
                source$
            });
        }
    }

    updateAllSelected(checked, checkedValue) {
        if (!(this.selectedSubmenus.includes(this.currentSubmenu))) {
            this.treeFacade.selectCascadeSubmenus(this.currentSubmenu);
        }

        this.spinner = true;
        if (checked) {
            this.treeFacade.selectCascadeSubmenus(checkedValue);
            this.refreshCascadeSubmenus();
        } else {
            this.treeFacade.deselectCascadeSubmenus(checkedValue);
            this.deselectCascadeSubmenus(checkedValue);
        }

        if (this.featureScope === 'Global' || this.featureScope === 'Viewer') {
            this.menuService.countMatchesForUpdateCascade(this.featureScope, this.data.effectiveDate, this.menuKey,
                this.path, this.selectedSubmenus)
                .pipe(
                    takeUntilDestroyed(this.destroyRef)
                )
                .subscribe({
                    next: (results) => {
                        this.matchCount = results;
                        this.spinner = false;
                    },
                    error: (error) => {
                        const errorMessage = 'Error searching parent nodes';
                        this.notificationService.error(errorMessage);
                        return;
                    }
                });
        }
    }

    findParents(item: TreeNode): TreeNode[] {
        return this.findParent(item, []);
    }

    findParent(node: TreeNode, parents: TreeNode[]): TreeNode[] {
        const parent = node.parent;
        if (node && !node.isRoot) {
            parents.push(parent);
            return this.findParent(parent, parents);
        }
        return parents;
    }

    findCurrentSubmenu() {
        try {
            this.currentSubmenu = this.parent.data.data.tagPath.split('/').filter(parent => parent !== '')[0];
            if (!(this.selectedSubmenus.includes(this.currentSubmenu))) {
                this.selectedSubmenus.push(this.currentSubmenu);
                this.treeFacade.selectCascadeSubmenus(this.currentSubmenu);
            }
        } catch (e) {}
    }

    buildAttributes(): void {
        try {
            const tagAttribute = {
                attributeGroup: 'CORE',
                dateUpdated: new Date().toISOString(),
                tag: 'TAG',
                name: 'Tag',
                jsonProperty: 'tag',
                description: 'Unique item identifier',
                options: null,
                type: 'TEXT',
                required: true,
                scope: 'GLOBAL',
                label: 'Tag',
                disabled: true,
                value: this.item.tag
            };
            this.attributes.push(tagAttribute);
            const typeAttribute = {
                attributeGroup: 'CORE',
                dateUpdated: new Date().toISOString(),
                tag: 'TYPE',
                name: 'Type',
                jsonProperty: 'type',
                description: 'Item type',
                type: 'TEXT',
                required: true,
                scope: 'GLOBAL',
                label: 'Type',
                disabled: true,
                value: this.item.typeName
            };
            this.attributes.push(typeAttribute);
            this.copyItemAttributes();
            this.orderAttributes();
        } catch(e) {}
    }

    copyItemAttributes() {
        this.item.attributes.forEach(attribute => this.attributes.push(attribute));
    }

    orderAttributes() {
        let orderedAttributes = [];
        for (const attribute of this.attributes) {
            switch (attribute.tag) {
                case 'NAME':
                    orderedAttributes[0] = attribute;
                    break;
                case 'TAG':
                    orderedAttributes[1] = attribute;
                    break;
                case 'TYPE':
                    orderedAttributes[2] = attribute;
                    break;
            }
        }
        const remainingAttributes = this.item.attributes.filter(attribute => this.attributes.includes(attribute));
        orderedAttributes = orderedAttributes.concat(remainingAttributes);
        this.attributes$.next(orderedAttributes);
    }

    onCascadeFilter($event: Event): void {
        const filterStr = ($event.target as HTMLInputElement).value;
        setTimeout(() => {
            this.filterCascadeSubmenus(filterStr);
        }, 725);
    }

    filterCascadeSubmenus(value: string): void {
        if (value === '') {
            this.cascadeSubmenus$.next(this.submenusCache);
        } else {
            this.cascadeSubmenus$.pipe(
                take(1),
                map((list: any) => {
                    const filteredNodes = list.filter(nd => nd['name'].toLowerCase().includes(value.toLowerCase()) ||
                        nd['id'].toLowerCase().includes(value.toLowerCase()));
                    this.cascadeSubmenus$.next(filteredNodes);
                }),
                takeUntilDestroyed(this.destroyRef)
            ).subscribe();
        }
    }

    listenForCascadeChange(): void {
        this.cascadeSubmenus$.asObservable()
            .pipe(
                map((values) => values ? this.filterSize = values.length : this.filterSize = 0),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe();
    }

    resetFilter(): void {
        this.searchForm.controls['searchTerm'].setValue('');
        this.cascadeSubmenus$.next(this.submenusCache);
    }

    setTopSelected() {
        this.topSelected.next(!!this.topSelected);
        this.bottomSelected.next(false);
    }

    setBottomSelected() {
        this.bottomSelected.next(!!this.bottomSelected);
        this.topSelected.next(false);
    }
}
